import { Injectable } from "@angular/core";

import { filter, take } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Device } from "@capacitor/device";
import { FirebaseAnalytics } from "@capacitor-community/firebase-analytics";
import {
  ActivatedRoute,
  ActivationEnd,
  NavigationEnd,
  Router,
  RouterEvent,
} from "@angular/router";
import { GetUserProfileUseCase } from "@app/core/user/domain/use-cases/get-user-profile.use-case";
import { User } from "@app/core/user/domain/user";

@Injectable({
  providedIn: "root",
})
export class AnalyticsService {
  analyticsEnabled = true;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private getUserProfile: GetUserProfileUseCase
  ) {}

  async initAnalytics() {
    if (this.isAnalyticsEnabled()) {
      if ((await Device.getInfo()).platform == "web") {
        await FirebaseAnalytics.initializeFirebase(environment.FIREBASE_CONFIG);
      }
    } else {
      if ((await Device.getInfo()).platform != "web")
        await this.disableAnalytics();
    }
    this.setUserToAnalytics();
    this.sendEachRouteToAnalytics();
  }

  setUserToAnalytics() {
    this.getUserProfile
      .execute()
      .pipe(take(1))
      .subscribe({
        next: (user: User) => {
          this.setUser(user.email);
        },
        error: () => {},
      });
  }

  sendEachRouteToAnalytics() {
    //This is in order to get the route like /match/:id
    //and send the id of the match as a param
    this.router.events
      .pipe(filter((e: any) => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        const params = {
          aiball_screen: this.getCurrentRouteSlug(),
          aiball_url: e.url,
        };
        this.setScreenName(params);
      });
  }

  getCurrentRouteSlug() {
    let currentRoute = this.router.routerState.root;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    return "/" + currentRoute.routeConfig?.path;
  }

  isAnalyticsEnabled() {
    return environment.ANALYTICS_ENABLED;
  }

  isAnalyticsDisabled() {
    return !this.isAnalyticsEnabled();
  }

  isDev() {
    return this.getEnvironment() === "DEV";
  }

  getEnvironment() {
    return environment.NAME;
  }

  async enableAnalytics() {
    await FirebaseAnalytics.setCollectionEnabled({
      enabled: true,
    });
  }

  async disableAnalytics() {
    await FirebaseAnalytics.setCollectionEnabled({
      enabled: false,
    });
  }

  async setUser(email: string) {
    if (this.isAnalyticsDisabled()) return;
    // Sets the user ID property.
    await FirebaseAnalytics.setUserId({
      userId: email,
    });
  }

  setProperty() {
    if (this.isAnalyticsDisabled()) return;
    //sets a user property to a given value.
    FirebaseAnalytics.setUserProperty({
      name: "framework",
      value: "angular",
    });
  }

  logEvent(logEvent: string, params: any = {}) {
    if (this.isAnalyticsDisabled()) return;
    //Logs an app event.
    FirebaseAnalytics.logEvent({
      name: logEvent,
      params: params,
    });
  }

  setScreenName(params: any = {}) {
    if (this.isAnalyticsDisabled()) return;
    this.logEvent("aiball_screen_view", params);
    //We dont use the FirebaseAnalytics.setScreenName
    //because we can't add parameters with setScreenName method
    //and we want the route with the url slut
  }

  toggleAnalytics() {
    if (this.isAnalyticsDisabled()) return;
    this.analyticsEnabled = !this.analyticsEnabled;
    FirebaseAnalytics.setCollectionEnabled({
      enabled: this.analyticsEnabled,
    });
  }
}
